import { observable, action, makeObservable, runInAction } from 'mobx';
import { notify } from 'services/NotificationService';
import mergeCsvWithPlays from 'helpers/mergeCsv/mergeCsvWithPlays';
import { IPlayUpdate } from 'interfaces/session/ISessionWithPlaysUpdateRequest';
import { IPlayMetadata, IBallFinal, IPlayer } from '@trackman/baseball-shared-components';
import { BaseStore } from './BaseStore';
import { SessionVerificationApiService } from '../services/data-services/SessionVerificationService';
import { IPlayWithBallFinal, IMergedPlayWithBallFinal } from '../interfaces/sessionVerification/plays/IPlayWithBallFinal';
import { ICSVRow } from '../interfaces/ICSV';
import { ISession } from '../interfaces/session/ISession';
import { ISessionUpdateRequest } from '../interfaces/session/ISessionUpdateRequest';
import { AutosaveStore } from './AutosaveStore';

export class SessionPlayStore extends BaseStore {
  private sessionVerifierService: SessionVerificationApiService;

  private autosaveStore: AutosaveStore;

  constructor(sessionVerifierService: SessionVerificationApiService, autosaveStore: AutosaveStore) {
    super();
    this.sessionVerifierService = sessionVerifierService;
    this.autosaveStore = autosaveStore;
    makeObservable(this);
  }

  @observable mergedPlays: IMergedPlayWithBallFinal[] = [];

  @observable apiPlays: IPlayWithBallFinal[] = [];

  @observable ballFinals: IBallFinal[] = [];

  @observable homeTeamRoster: IPlayer[] = [];

  @observable awayTeamRoster: IPlayer[] = [];

  @action resetPlays = () => {
    this.mergedPlays = [];
    this.apiPlays = [];
  };

  @action loadPlays = async (sessionId: string) => {
    try {
      const playsTask = this.sessionVerifierService.getPlaysForSession(sessionId);
      const ballFinalsTask = this.sessionVerifierService.getBallFinalsForSession(sessionId);
      const [plays, ballFinals] = await Promise.all([playsTask, ballFinalsTask]);
      plays.sort((a, b) => a.taggerBehavior.sequenceNumber - b.taggerBehavior.sequenceNumber);
      this.ballFinals = ballFinals;
      this.apiPlays = this.connectPlaysWithBallFinals(plays, ballFinals);
      this.mapApiPlays();
    } catch (err: unknown) {
      this.resetPlays();
      if (err instanceof Error) {
        notify('Error', err.message, 'error');
      }
    }
  };

  @action loadRosters = async (homeTeamId: string, awayTeamId: string) => {
    try {
      const homeTask = this.sessionVerifierService.getRoster(homeTeamId);
      const awayTask = this.sessionVerifierService.getRoster(awayTeamId);
      const [home, away] = await Promise.all([homeTask, awayTask]);
      runInAction(() => {
        this.homeTeamRoster = home;
        this.awayTeamRoster = away;
      });
    } catch (err: unknown) {
      if (err instanceof Error) {
        notify('Error', err.message, 'error');
      }
    }
  };

  @action mapApiPlays = () => {
    this.mergedPlays = this.apiPlays.map((p) => ({
      ...p,
      isChanged: false,
      isMarkedForDeletion: false,
      isCreated: false,
      changedValues: [],
      isBallTrackingChanged: false,
      isPlayerTrackingChanged: false,
    }));
  };

  @action processCsvRows = (rows: ICSVRow[], session: ISession) => {
    try {
      const oldPlays = this.mergedPlays.map((mergedPlay) => {
        return {
          playId: mergedPlay.play.playId,
          baseRunners: mergedPlay.play.baseRunners,
          fieldingSequence: mergedPlay.play.fieldingSequence,
          players: {
            firstBaseman: mergedPlay.play.players.firstBaseman,
            secondBaseman: mergedPlay.play.players.secondBaseman,
            thirdBaseman: mergedPlay.play.players.thirdBaseman,
            shortStopper: mergedPlay.play.players.shortStopper,
            leftFielder: mergedPlay.play.players.leftFielder,
            rightFielder: mergedPlay.play.players.rightFielder,
            centerFielder: mergedPlay.play.players.centerFielder,
          },
        };
      });

      this.mergedPlays = mergeCsvWithPlays(rows, this.apiPlays, session, this.ballFinals, this.homeTeamRoster, this.awayTeamRoster);

      this.mergedPlays = this.mergedPlays.map((mergedPlay) => {
        const oldPlay = oldPlays.find((oldPlay) => oldPlay.playId && oldPlay.playId === mergedPlay.play.playId);

        if (oldPlay) {
          return {
            ...mergedPlay,
            play: {
              ...mergedPlay.play,
              ...oldPlay,
              players: {
                ...mergedPlay.play.players,
                ...oldPlay?.players,
              },
            },
          };
        }
        return mergedPlay;
      });

      this.autosaveStore.clear();
    } catch (err: unknown) {
      this.mapApiPlays();
      if (err instanceof Error) {
        notify('Error', err.message, 'error');
      }
    }
  };

  @action updateSession = async (sessionUpdate: ISessionUpdateRequest, plays: IMergedPlayWithBallFinal[]) => {
    try {
      const response = await this.sessionVerifierService.updateSession({
        session: sessionUpdate,
        plays: plays.map(
          (p) =>
            ({
              play: p.play,
              isBallTrackingChanged: p.isBallTrackingChanged,
              isPlayerTrackingChanged: p.isPlayerTrackingChanged,
            } as IPlayUpdate)
        ),
      });
      if (!response.schemaValidationForPlaysOccured || !response.schemaValidationForSessionOccured) {
        notify('Warning', "Data was updated, but we couldn't validate it, so there is no guarantee it will be processed", 'info');
      }
      return true;
    } catch (err: unknown) {
      if (err instanceof Error) {
        notify('Error', err.message, 'error', (err.message as string).length > 120 ? 'toast-large' : undefined);
      }
      return false;
    }
  };

  connectPlaysWithBallFinals = (plays: IPlayMetadata[], ballFinals: IBallFinal[]) => {
    const result = [] as IPlayWithBallFinal[];
    plays.forEach((play) => {
      const playWithBallFinals = { play, ballFinals: [] } as IPlayWithBallFinal;
      if (play.playId) {
        playWithBallFinals.ballFinals = ballFinals.filter((bf) => bf.playId === play.playId);
      }
      result.push(playWithBallFinals);
    });
    return result;
  };
}
